/**
 * Browser mirror of functions/lib/nonRequestableFields.js.
 *
 * Create React App refuses imports from outside src/, so the launch gate the
 * operator sees and the launch gate the server enforces cannot share one file.
 * They share a test instead: src/utils/nonRequestableFields.test.js runs the
 * same fixtures through both and fails the moment they disagree. Change one,
 * change the other.
 */

// Mirrors REQUEST_FIELD_ALIASES in campaignLaunchSafety.js / draftGuards.js /
// initialScriptSafety.js. A configured label reaches its whole alias group, so
// a Note column headed "Loading Docks" also refuses a script asking for "dock
// doors" or "ramp doors".
const REQUEST_FIELD_ALIASES = {
  total_sf: ["total sf", "available sf", "square footage"],
  rent: ["rent sf yr", "rent per sf", "asking rent", "rental rate", "lease rate"],
  op_ex: ["ops ex", "op ex", "opex", "operating expenses", "nnn expenses", "nnn charges"],
  drive_ins: ["drive ins", "drive in doors", "drive in door", "ramp doors", "ramp door"],
  docks: ["docks", "loading docks", "loading dock", "dock doors", "dock door"],
  ceiling_height: ["ceiling ht", "ceiling height", "clear height"],
  power: ["available power", "power details", "power requirements", "power capacity", "electrical service", "electrical capacity", "amperage", "voltage"],
  year_built: ["year built", "construction year"],
  flyer: ["flyer", "marketing package", "brochure", "floor plan", "floorplan"],
};

const REQUEST_LABEL_ALIASES = { power: ["power"] };

const MAX_FIELD_COUNT = 250;
const MAX_LABEL_LENGTH = 160;

const ROLE_GUIDANCE = {
  note: {
    name: "Note",
    phrase: "is configured as Note",
    behaviour: "SiteSift records it if a broker mentions it but never asks for it",
  },
  skip: {
    name: "Skip",
    phrase: "is configured as Skip",
    behaviour: "SiteSift ignores this column entirely and never asks for it",
  },
  formula: {
    name: "formula",
    phrase: "is configured as a calculated formula column",
    behaviour: "SiteSift computes it from the other columns and never asks for it",
  },
};

function normalizeScopeText(value) {
  return String(value || "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .split(" ")
    .map(token => ({
      street: "st", road: "rd", avenue: "ave", boulevard: "blvd", highway: "hwy",
      drive: "dr", lane: "ln", court: "ct", parkway: "pkwy", suite: "ste",
      north: "n", south: "s", east: "e", west: "w",
    }[token] || token))
    .join(" ");
}

function containsNormalizedPhrase(text, phrase) {
  const needle = normalizeScopeText(phrase);
  if (!needle) return false;
  return ` ${text} `.includes(` ${needle} `);
}

function scriptRequestsGroup(text, normalizedText, group, aliases) {
  if (aliases.some(alias => containsNormalizedPhrase(normalizedText, alias))) return true;
  if (group !== "power") return false;
  return /(?:^|\n)\s*[-*•]\s*(?:available\s+)?power\b/i.test(text)
    || /\b(?:send|share|provide|confirm|include|need|request)\b\s+(?:(?:the|available|property|electrical)\s+){0,3}power\b/i.test(text);
}

// The alias groups a configured label belongs to. A label that matches no
// canonical group (a custom column such as "Site Notes") still matches on its
// own literal text.
function groupsForLabel(label) {
  const normalizedLabel = normalizeScopeText(label);
  if (!normalizedLabel) return [];
  return Object.entries(REQUEST_FIELD_ALIASES)
    .filter(([group, aliases]) => [...aliases, ...(REQUEST_LABEL_ALIASES[group] || [])]
      .some(alias => containsNormalizedPhrase(normalizedLabel, alias)))
    .map(([group]) => group);
}

function normalizeLabel(value) {
  if (typeof value !== "string") return "";
  const label = value.trim().replace(/\s+/g, " ");
  if (!label || label.length > MAX_LABEL_LENGTH) return "";
  return label;
}

function pushField(collected, seen, label, role) {
  const normalized = normalizeLabel(label);
  if (!normalized) return;
  if (collected.length >= MAX_FIELD_COUNT) return;
  const key = `${role}:${normalized.toLocaleLowerCase()}`;
  if (seen.has(key)) return;
  seen.add(key);
  collected.push({ label: normalized, role });
}

/**
 * Every column an advisor configured as Note, Skip or formula, as
 * `{ label, role }` in configuration order: canonical Notes, calculated
 * columns, then custom columns.
 *
 * Note the asymmetry in the stored shape: ColumnMappingStep.handleConfirm
 * records canonical Note columns in `neverRequest` and calculated columns in
 * `formulaFields`, but returns early on a canonical Skip column, so a skipped
 * canonical column leaves no trace in the config at all. Only custom columns
 * carry an explicit `skip` mode. Nothing here invents the columns that were
 * never written down; it reports exactly the roles the config preserves.
 */
export function deriveNonRequestableFields(columnConfig) {
  if (!columnConfig || typeof columnConfig !== "object" || Array.isArray(columnConfig)) {
    return [];
  }

  const {
    neverRequest = [],
    formulaFields = [],
    customFields = {},
    mappings = {},
  } = columnConfig;

  const labelFor = key => (
    (mappings && typeof mappings === "object" && typeof mappings[key] === "string" && mappings[key])
    || key
  );

  const collected = [];
  const seen = new Set();

  if (Array.isArray(neverRequest)) {
    neverRequest.forEach(key => pushField(collected, seen, labelFor(key), "note"));
  }
  if (Array.isArray(formulaFields)) {
    formulaFields.forEach(key => pushField(collected, seen, labelFor(key), "formula"));
  }
  if (customFields && typeof customFields === "object" && !Array.isArray(customFields)) {
    Object.entries(customFields).forEach(([header, config]) => {
      const mode = String(config?.mode || "").trim().toLocaleLowerCase();
      if (mode === "note") pushField(collected, seen, header, "note");
      if (mode === "skip") pushField(collected, seen, header, "skip");
    });
  }

  return collected;
}

/** Operator guidance: what the field is, why it is not asked, how to fix it. */
export function describeNonRequestableConflict(field = {}) {
  const label = normalizeLabel(field.label) || "This column";
  const role = ROLE_GUIDANCE[String(field.role || "").trim().toLocaleLowerCase()]
    || ROLE_GUIDANCE.note;
  return `${label} ${role.phrase} — ${role.behaviour}.`
    + " Remove it from the message or change the column to Ask.";
}

/**
 * The non-requestable fields this script actually asks for, in configuration
 * order and without repeats.
 */
export function findNonRequestableRequests(script, fields) {
  const text = String(script || "");
  if (!text.trim() || !Array.isArray(fields)) return [];
  const normalizedText = normalizeScopeText(text);

  const matched = [];
  const seen = new Set();
  for (const field of fields) {
    const label = normalizeLabel(field?.label);
    if (!label) continue;
    const role = String(field?.role || "").trim().toLocaleLowerCase();
    if (!ROLE_GUIDANCE[role]) continue;

    const key = `${role}:${label.toLocaleLowerCase()}`;
    if (seen.has(key)) continue;

    const requested = containsNormalizedPhrase(normalizedText, label)
      || groupsForLabel(label).some(group => scriptRequestsGroup(
        text,
        normalizedText,
        group,
        REQUEST_FIELD_ALIASES[group]
      ));
    if (!requested) continue;

    seen.add(key);
    matched.push({ label, role });
  }
  return matched;
}

/** Guidance for every non-requestable field this script asks for. */
export function describeNonRequestableRequests(script, fields) {
  return findNonRequestableRequests(script, fields).map(describeNonRequestableConflict);
}

/**
 * Normalise a browser-supplied non-requestable contract. Returns null when the
 * value is present but malformed, so a launch path can refuse rather than
 * silently drop the rule (an absent contract is `[]`, a broken one is null).
 */
export function normalizeNonRequestableFieldsInput(value) {
  if (value === undefined || value === null) return [];
  if (!Array.isArray(value) || value.length > MAX_FIELD_COUNT) return null;
  const normalized = [];
  const seen = new Set();
  for (const entry of value) {
    if (!entry || typeof entry !== "object" || Array.isArray(entry)) return null;
    const label = normalizeLabel(entry.label);
    if (!label) return null;
    const role = String(entry.role || "").trim().toLocaleLowerCase();
    if (!ROLE_GUIDANCE[role]) return null;
    const key = `${role}:${label.toLocaleLowerCase()}`;
    if (seen.has(key)) continue;
    seen.add(key);
    normalized.push({ label, role });
  }
  return normalized;
}

export const NON_REQUESTABLE_ROLES = Object.keys(ROLE_GUIDANCE);
